import { supabase } from '../lib/supabase'
import type { Project } from '../types/index'

function rowToProject(r: Record<string, unknown>): Project {
  return {
    id:          r.id as string,
    title:       r.title as string,
    clientId:    (r.client_id as string) || '',
    clientName:  (r.client_name as string) || '',
    status:      (r.status as Project['status']) || 'planning',
    priority:    (r.priority as Project['priority']) || 'medium',
    dueDate:     (r.due_date as string) || '',
    assignees:   (r.assignees as string[]) || [],
    description: (r.description as string) || '',
    progress:    (r.progress as number) || 0,
    tags:        (r.tags as string[]) || [],
  }
}

export async function getProjects(): Promise<Project[]> {
  const { data, error } = await supabase.from('projects').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map(rowToProject)
}

export async function getProjectsByClient(clientId: string): Promise<Project[]> {
  const { data, error } = await supabase.from('projects').select('*')
    .eq('client_id', clientId).order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map(rowToProject)
}

export async function createProject(p: Omit<Project, 'id'>): Promise<Project> {
  const { data, error } = await supabase.from('projects').insert({
    title: p.title, client_id: p.clientId || null, client_name: p.clientName,
    status: p.status, priority: p.priority, due_date: p.dueDate,
    assignees: p.assignees, description: p.description, progress: p.progress, tags: p.tags,
  }).select().single()
  if (error) throw error
  return rowToProject(data)
}

export async function updateProject(id: string, p: Partial<Project>): Promise<void> {
  const updates: Record<string, unknown> = {}
  if (p.title       !== undefined) updates.title       = p.title
  if (p.clientId    !== undefined) updates.client_id   = p.clientId || null
  if (p.clientName  !== undefined) updates.client_name = p.clientName
  if (p.status      !== undefined) updates.status      = p.status
  if (p.priority    !== undefined) updates.priority    = p.priority
  if (p.dueDate     !== undefined) updates.due_date    = p.dueDate
  if (p.assignees   !== undefined) updates.assignees   = p.assignees
  if (p.description !== undefined) updates.description = p.description
  if (p.progress    !== undefined) updates.progress    = p.progress
  if (p.tags        !== undefined) updates.tags        = p.tags
  const { error } = await supabase.from('projects').update(updates).eq('id', id)
  if (error) throw error
}

export async function deleteProject(id: string): Promise<void> {
  const { error } = await supabase.from('projects').delete().eq('id', id)
  if (error) throw error
}
